import { Button, CloseButton, DropdownMenu } from 'components/ui'
import { useBuilderWidget, useBuilderCategoryMode } from 'components/TasksV2/TaskCreation/contexts/BuilderWidgetContext'
import { useCreationText } from 'components/TasksV2/TaskCreation/hooks/useCreationText'
import React, { useState, useRef, useEffect } from 'react'
import { Box, Flex, Input, Popover, PopoverTrigger, PopoverContent, PopoverBody, Text } from '@chakra-ui/core'
import { Controller, useFormContext } from 'react-hook-form'
import colors from 'constants/colors'
import { WidgetWrapper } from 'components/TasksV2/UI/WidgetWrapper'
import { ChevronDown, Trash2 } from 'lucide-react'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import useLocaleText from 'components/useLocaleText'
import { DEFAULT_CATEGORIES, TaskCategory as Category, categoryOptions } from 'components/TasksV2/model/categoryNames'
import {
  useTaskCategories,
  loadCategoriesFromStorage,
  saveCategoriesToStorage,
} from 'components/TasksV2/hooks/useTaskCategories'

interface CategorySelectorProps {
  subtaskPath: string
  defaultCategory?: string
}

export { clearTaskCategoriesFromStorage } from 'components/TasksV2/hooks/useTaskCategories'

export const CategorySelector: React.FC<CategorySelectorProps> = ({ subtaskPath, defaultCategory }) => {
  const { control, watch, setValue } = useFormContext()
  const theme = useSelector((state: RootState) => state?.general?.theme)
  const rid = useSelector((state: RootState) => state?.config?.config?.rid)
  const builder = useBuilderWidget()
  const categoryManagedExternally = useBuilderCategoryMode()
  const t = useCreationText()

  const [showNewCategory, setShowNewCategory] = useState(false)
  const [categoryDraft, setCategoryDraft] = useState('')
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null)

  const { categories: customCategories, setCategories: setCustomCategories } = useTaskCategories()

  const inputRef = useRef<HTMLInputElement>(null)

  const t_add_category = useLocaleText('add_category')
  const t_select_category = useLocaleText('add_category')
  const t_add = useLocaleText('add')

  const selectedCategoryName = watch(`${subtaskPath}.category`)

  const allCategories = categoryOptions(rid, customCategories) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddCategory = () => {
    const value = inputRef.current?.value.trim() || ''

    if (value && !allCategories.some((cat) => cat.name === value)) {
      const newCat: Category = {
        name: value,
      }
      const updatedCategories = [...customCategories, newCat]
      setCustomCategories(updatedCategories)
      saveCategoriesToStorage(updatedCategories)
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }
  }

  const handleSelectCategory = (categoryName: string, onClose: () => void) => {
    setValue(`${subtaskPath}.category`, categoryName)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
    onClose()
  }

  const handlePopoverClose = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleClearCategory = (e: React.MouseEvent) => {
    e.stopPropagation()
    setValue(`${subtaskPath}.category`, undefined)
  }

  const handleDeleteCustomCategory = (categoryName: string, e: React.MouseEvent) => {
    e.stopPropagation()

    const updatedCategories = customCategories.filter((cat) => cat.name !== categoryName)

    setCustomCategories(updatedCategories)
    saveCategoriesToStorage(updatedCategories)
    if (selectedCategoryName === categoryName) {
      setValue(`${subtaskPath}.category`, undefined)
    }
  }

  // When a task is loaded for editing/cloning, the subtask may have a category that
  // isn't in localStorage or the default list. Persist it to localStorage so it's
  // visible and available for future selections.
  useEffect(() => {
    if (!selectedCategoryName) return

    const isKnown =
      DEFAULT_CATEGORIES.includes(selectedCategoryName) || customCategories.some((c) => c.name === selectedCategoryName)

    if (!isKnown) {
      const stored = loadCategoriesFromStorage()
      const alreadyStored = stored.some((c) => c.name === selectedCategoryName)
      if (!alreadyStored) {
        const updated = [...stored, { name: selectedCategoryName }]
        saveCategoriesToStorage(updated)
        setCustomCategories(updated)
      } else {
        setCustomCategories(stored)
      }
    }
  }, [customCategories, selectedCategoryName, setCustomCategories])

  if (categoryManagedExternally) {
    return (
      <Controller
        name={`${subtaskPath}.category`}
        control={control}
        defaultValue={defaultCategory || selectedCategoryName || ''}
        as={<Box hidden />}
      />
    )
  }

  if (builder) {
    const createCategory = () => {
      const value = categoryDraft.trim()

      if (!value) return
      if (!allCategories.some((category) => category.name === value)) {
        const updated = [...customCategories, { name: value }]
        setCustomCategories(updated)
        saveCategoriesToStorage(updated)
      }
      setValue(`${subtaskPath}.category`, value)
      setCategoryDraft('')
      setShowNewCategory(false)
    }
    return (
      <div className="creation-category-editor">
        <div className="creation-section-heading">
          <strong>{t.category}</strong>
        </div>
        {!!allCategories.length && (
          <div className="creation-category-select-row">
            <div className="creation-category-dropdown">
              <DropdownMenu
                usePortal
                options={[
                  { id: '', value: '', label: <>{t.noCategory}</> },
                  ...allCategories.map(({ name }) => ({ id: name, value: name, label: <>{name}</> })),
                ]}
                selectedId={selectedCategoryName || ''}
                onSelect={({ value }) => setValue(`${subtaskPath}.category`, value)}
                triggerVariant="secondary"
                triggerButtonProps={{ 'aria-label': t.category, width: '100%', justifyContent: 'space-between' }}
                triggerContent={
                  <>
                    <Text isTruncated>{selectedCategoryName || t.noCategory}</Text>
                    <ChevronDown size={16} style={{ flexShrink: 0, marginInlineStart: 8 }} />
                  </>
                }
                menuWidth={280}
              />
            </div>
            {selectedCategoryName && customCategories.some((category) => category.name === selectedCategoryName) && (
              <Button
                variant="icon"
                height="auto"
                type="button"
                className="creation-category-remove"
                color="#c74740"
                _hover={{ color: '#a82d28', bg: '#fdebea' }}
                aria-label={`${t.remove} ${selectedCategoryName}`}
                title={`${t.remove} ${selectedCategoryName}`}
                onClick={(event) => handleDeleteCustomCategory(selectedCategoryName, event)}
              >
                <Trash2 size={16} />
              </Button>
            )}
            <Button
              variant="secondary"
              height="auto"
              type="button"
              className="creation-new-category"
              onClick={() => setShowNewCategory(!showNewCategory)}
            >
              + {t.newCategory}
            </Button>
          </div>
        )}
        {(!allCategories.length || showNewCategory) && (
          <div className="creation-category-input">
            <input
              aria-label={t.newCategory}
              placeholder={t.categoryPlaceholder}
              value={categoryDraft}
              onChange={(event) => setCategoryDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  createCategory()
                }
              }}
            />
            <Button
              variant="primary"
              height="auto"
              type="button"
              isDisabled={!categoryDraft.trim()}
              onClick={createCategory}
            >
              {t.createCategory}
            </Button>
          </div>
        )}
        <Controller name={`${subtaskPath}.category`} control={control} defaultValue="" as={<Box />} />
      </div>
    )
  }

  return (
    <Flex alignItems="center" flexDir="row" justifyContent="flex-end">
      <Popover placement="bottom-end" closeOnBlur onClose={handlePopoverClose}>
        {({ onClose = () => {} }) => (
          <>
            <PopoverTrigger>
              <Flex style={{ columnGap: '10px' }} alignItems="center" cursor="pointer">
                <WidgetWrapper
                  p="10px"
                  borderRadius="20px"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  w="max-content"
                  maxW="200px"
                  bg={colors.greyLight5}
                  boxShadow="none"
                  border="1px solid #f5f5f5"
                >
                  <Flex alignItems="center" flexDir="row" w="100%" style={{ columnGap: '10px' }}>
                    <Flex alignItems="center">
                      {selectedCategoryName ? (
                        <Text fontSize="13px" isTruncated>
                          {selectedCategoryName}
                        </Text>
                      ) : (
                        <Text fontSize="13px" color={colors.analyticGrey}>
                          {t_select_category}
                        </Text>
                      )}
                    </Flex>

                    <Flex alignItems="center" style={{ columnGap: '8px' }}>
                      {selectedCategoryName && (
                        <CloseButton
                          type="button"
                          aria-label={`${t.remove} ${selectedCategoryName}`}
                          onClick={handleClearCategory}
                          minWidth="24px"
                          height="24px"
                          padding="4px"
                          color={colors.greyDark}
                        />
                      )}
                      <ChevronDown size={16} />
                    </Flex>
                  </Flex>
                </WidgetWrapper>
              </Flex>
            </PopoverTrigger>

            <PopoverContent
              fontSize="14px"
              fontWeight="500"
              borderRadius="8px"
              border="1px solid"
              borderColor={colors.greyBorder}
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
              _focus={{ outline: 'none' }}
              w="300px"
              zIndex={99999}
              minH="200px"
            >
              <PopoverBody p={0}>
                <Box pt="10px" px="12px" pb="10px">
                  <Flex alignItems="center" style={{ columnGap: '8px' }}>
                    <Input
                      ref={inputRef}
                      autoFocus
                      borderRadius="8px"
                      border="1px solid"
                      borderColor={colors.greyBorder}
                      px="10px"
                      py="5px"
                      fontSize="13px"
                      fontWeight="500"
                      color={colors.greyDark}
                      placeholder={t_add_category}
                      defaultValue=""
                      _hover={{ borderColor: colors.greyBorder }}
                      _focus={{ borderColor: colors.greyBorder, outline: 'none' }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddCategory()
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      bg={theme?.elementsColor}
                      color="white"
                      onClick={handleAddCategory}
                      px="10px"
                    >
                      {t_add}
                    </Button>
                  </Flex>
                </Box>
                <Box maxH="300px" overflowY="auto">
                  {allCategories.map((category, index) => {
                    // Only custom categories can be deleted (not default ones)
                    const isCustomCategory = customCategories.some((cat) => cat.name === category.name)
                    const isHovered = hoveredCategory === category.name

                    return (
                      <Flex
                        key={index}
                        py="10px"
                        px="12px"
                        style={{ columnGap: '10px' }}
                        onClick={() => handleSelectCategory(category.name, onClose)}
                        flexDir="row"
                        alignItems="center"
                        justifyContent="space-between"
                        bg={
                          selectedCategoryName === category.name
                            ? colors.greyLight5
                            : isHovered
                            ? colors.greyLight5
                            : 'transparent'
                        }
                        cursor="pointer"
                        onMouseEnter={() => setHoveredCategory(category.name)}
                        onMouseLeave={() => setHoveredCategory(null)}
                      >
                        <Text fontSize="13px">{category.name}</Text>

                        {isCustomCategory && (
                          <CloseButton
                            type="button"
                            aria-label={`${t.remove} ${category.name}`}
                            onClick={(e: React.MouseEvent) => handleDeleteCustomCategory(category.name, e)}
                            minWidth="24px"
                            height="24px"
                            padding="4px"
                            color={colors.greyDark}
                            style={{ marginInlineStart: '10px' }}
                          />
                        )}
                      </Flex>
                    )
                  })}
                </Box>
              </PopoverBody>
            </PopoverContent>
          </>
        )}
      </Popover>

      {/* Controller for form registration */}
      <Controller name={`${subtaskPath}.category`} control={control} defaultValue="" as={<Box />} />
    </Flex>
  )
}
